import { ModelTypes } from '../../../../types'
import { getIsViewOnly } from '../../../../../../utils/viewOnly'
import { InputViewOnly } from '../../../../../ViewOnly/InputViewOnly'
import { RuleBlock } from './models/RuleBlock'
import { RuleWrappedBlock } from './models/RuleWrappedBlock'
import { RuleBlockArray } from './models/RuleBlockArray'
import { RuleBlockDict } from './models/RuleBlockDict'
import { RuleBool } from './models/RuleBool'
import { RuleEnum } from './models/RuleEnum'
import { RuleInt } from './models/RuleInt'
import { RuleIntRange } from './models/RuleIntRange'
import { RuleNum } from './models/RuleNum'
import { RuleSuggestedValues } from './models/RuleSuggestedValues'
import { RuleString } from './models/RuleString'
import { RuleTextarea } from './models/RuleTextarea'
import { RuleDateTime } from './models/RuleDateTime'
import { RuleDouble } from './models/RuleDouble'
import { RuleDuration } from './models/RuleDuration'

const VIEW_ONLY_TYPES = new Set([
    ModelTypes.ENUM,
    ModelTypes.INTEGER,
    ModelTypes.INTEGER_RANGE,
    ModelTypes.NUMBER,
    ModelTypes.STRING,
    ModelTypes.TEXT,
    ModelTypes.DATETIME,
    ModelTypes.TIME,
    ModelTypes.DOUBLE,
    ModelTypes.DURATION,
])

const createViewOnlyInput = (
    name: string,
    schema: Record<string, any>,
    config: Record<string, any>,
    path: string
) => (
    <InputViewOnly
        name={name}
        title={schema?.name}
        value={config?.[name]}
        key={name + path}
    />
)

export const buildRuleCard = (
    path: string,
    name: string,
    schema: any = {},
    config: any = {},
    wrapper?: string,
    isParentDisabled?: boolean,
    levelDeep?: number,
    element_id?: string,
    inCard?: boolean,
    isRuleActive?: boolean,
    pathWithElementID?: string
) => {
    const elementType = schema.type ?? 'default'
    const isViewOnly = getIsViewOnly(schema.is_access_restricted)
    const hasSuggestedValues = !!schema.suggested_values_path

    if (schema.is_generated_by_product) {
        return <></>
    }

    if (isViewOnly && VIEW_ONLY_TYPES.has(elementType)) {
        return createViewOnlyInput(name, schema, config, path)
    }

    const editableElements: Record<string, JSX.Element> = {
        [ModelTypes.BLOCK]: wrapper ? (
            <RuleWrappedBlock
                key={path + pathWithElementID + name}
                dataPath={path}
                pathWithElementID={pathWithElementID}
                title={schema.name}
                dataSchema={schema}
                name={name}
                wrapper={wrapper}
                isParentDisabled={isParentDisabled}
                levelDeep={levelDeep}
                isViewOnly={isViewOnly}
                element_id={element_id}
                inCard={inCard}
                isRuleActive={isRuleActive}
            />
        ) : (
            <RuleBlock
                key={path + pathWithElementID + name}
                dataPath={path}
                pathWithElementID={pathWithElementID}
                title={schema?.name}
                dataSchema={schema}
                name={name}
                wrapper={wrapper}
                isParentDisabled={isParentDisabled}
                isViewOnly={isViewOnly}
                element_id={element_id}
                inCard={inCard}
                isRuleActive={isRuleActive}
            />
        ),
        [ModelTypes.BLOCK_ARRAY]: (
            <RuleBlockArray
                key={path + pathWithElementID + name}
                dataPath={path}
                pathWithElementID={pathWithElementID}
                schema={schema}
                name={name}
                wrapper={wrapper}
                levelDeep={levelDeep}
                isViewOnly={isViewOnly}
                element_id={element_id}
                inCard={inCard}
                isRuleActive={isRuleActive}
                isParentDisabled={isParentDisabled}
            />
        ),
        [ModelTypes.BLOCK_DICTIONARY]: (
            <RuleBlockDict
                key={path + pathWithElementID + name}
                dataPath={path}
                pathWithElementID={pathWithElementID}
                dataSchema={schema}
                name={name}
                wrapper={wrapper}
                isViewOnly={isViewOnly}
                element_id={element_id}
                inCard={inCard}
                isRuleActive={isRuleActive}
            />
        ),
        [ModelTypes.FLAG]: (
            <RuleBool
                dataPath={path}
                dataSchema={schema}
                name={name}
                isViewOnly={isViewOnly}
                key={name + path}
            />
        ),
        [ModelTypes.BOOL]: (
            <RuleBool
                dataPath={path}
                dataSchema={schema}
                name={name}
                isViewOnly={isViewOnly}
                key={name + path}
            />
        ),
        [ModelTypes.ENUM]: (
            <RuleEnum
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                key={name + path}
            />
        ),
        [ModelTypes.INTEGER]: (
            <RuleInt
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.INTEGER_RANGE]: (
            <RuleIntRange
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.NUMBER]: (
            <RuleNum
                key={path + pathWithElementID + name}
                dataPath={path}
                pathWithElementID={pathWithElementID}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
            />
        ),
        [ModelTypes.STRING]: hasSuggestedValues ? (
            <RuleSuggestedValues
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ) : (
            <RuleString
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.TEXT]: (
            <RuleTextarea
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.DATETIME]: (
            <RuleDateTime
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                isParentDisabled={isParentDisabled}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.TIME]: (
            <RuleDateTime
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                isParentDisabled={isParentDisabled}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.DOUBLE]: (
            <RuleDouble
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ),
        [ModelTypes.DURATION]: (
            <RuleDuration
                key={path + pathWithElementID + name}
                dataPath={path}
                dataSchema={schema}
                name={name}
                wrapper={wrapper || ''}
                pathWithElementID={pathWithElementID}
            />
        ),
    }

    return editableElements[elementType] ?? <></>
}
